import * as THREE from "three";
import { MeshLineGeometry as MeshLine, MeshLineMaterial } from "meshline";

const ballRadius = 4.294;
const arcOffset = 0.01;

export function generateArc({
  center,
  axis,
  radius,
  angleSweep,
  segments = 256,
  handednessFactor = 1,
  arcOffset = 0.005,
}) {
  const arcPoints = [];

  const start = center.clone().normalize();
  const rotationAxis = axis.clone().normalize();

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = handednessFactor * angleSweep * (t - 0.5);
    const point = start
      .clone()
      .applyAxisAngle(rotationAxis, angle)
      .normalize()
      .multiplyScalar(radius + arcOffset);
    arcPoints.push(point);
  }

  return arcPoints;
}

export function createArcMesh(points, color = 0xffffff, width = 0.05) {
  const line = new MeshLine();
  line.setPoints(points);
  const material = new MeshLineMaterial({
    color: new THREE.Color(color),
    lineWidth: width,
  });
  return new THREE.Mesh(line, material);
}

export function sphericalLawOfCosines(a, b, c) {
  // a, b, c are angles (radians) corresponding to arc lengths / radius
  const cosA =
    (Math.cos(a) - Math.cos(b) * Math.cos(c)) / (Math.sin(b) * Math.sin(c));
  return Math.acos(THREE.MathUtils.clamp(cosA, -1, 1));
}

export function sphericalLawOfSines(
  oppositeAngle,
  oppositeLength,
  knownLength
) {
  // sin(A)/a = sin(B)/b → solve for angle B
  const sinB = (Math.sin(oppositeAngle) * knownLength) / oppositeLength;
  return Math.asin(THREE.MathUtils.clamp(sinB, -1, 1));
}

export function rotateAroundNormal(vector, normal, angleRad) {
  return vector.clone().applyAxisAngle(normal, angleRad).normalize();
}

// Helper para validar un número
function isValidNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) && value !== -1;
}

// Helper para validar un número positivo
function isValidPositiveNumber(value) {
  return isValidNumber(value) && value > 0;
}

export function isValidDualAngleLayout({
  pinToPapDistance,
  pinAngle,
  valAngle,
}) {
  return (
    isValidPositiveNumber(pinToPapDistance) &&
    isValidNumber(pinAngle) &&
    isValidNumber(valAngle)
  );
}

export function isValidVLSLayout({
  pinToPapDistance,
  pasToPapDistance,
  pinBufferDistance,
}) {
  return (
    isValidPositiveNumber(pinToPapDistance) &&
    isValidPositiveNumber(pasToPapDistance) &&
    isValidPositiveNumber(pinBufferDistance)
  );
}

export function isValid2LSLayout({
  pinToPapDistance,
  mbToPapDistance,
  pinToCenter,
  playerPapOffset,
}) {
  const hasValidDistances =
    isValidPositiveNumber(pinToPapDistance) &&
    isValidPositiveNumber(mbToPapDistance) &&
    isValidNumber(pinToCenter);

  const hasValidPapOffset =
    playerPapOffset &&
    isValidNumber(playerPapOffset.horizontal) &&
    isValidNumber(playerPapOffset.vertical);

  return hasValidDistances && hasValidPapOffset;
}

export function isValidPALLayout({
  pinToPapDistance,
  mbToPapDistance,
  pinToCenter,
  playerPapOffset,
}) {
  const hasValidDistances =
    isValidPositiveNumber(pinToPapDistance) &&
    isValidPositiveNumber(mbToPapDistance) &&
    isValidPositiveNumber(pinToCenter);

  const hasValidPapOffset =
    playerPapOffset &&
    isValidNumber(playerPapOffset.horizontal) &&
    isValidNumber(playerPapOffset.vertical);

  return hasValidDistances && hasValidPapOffset;
}

export function intersectTwoDistanceCirclesOnSphere(
  centerA: THREE.Vector3,
  centerB: THREE.Vector3,
  dA: number,
  dB: number,
  radius: number
): { q1: THREE.Vector3 | null, q2: THREE.Vector3 | null } {
  if (
    !centerA ||
    !centerB ||
    typeof centerA.clone !== "function" ||
    typeof centerB.clone !== "function"
  ) {
    return { q1: null, q2: null };
  }

  if (![dA, dB, radius].every((value) => Number.isFinite(value))) {
    return { q1: null, q2: null };
  }

  if (radius <= 0) {
    return { q1: null, q2: null };
  }

  const aDir = centerA.clone().normalize();
  const bDir = centerB.clone().normalize();

  const thetaA = dA / radius;
  const thetaB = dB / radius;

  const cA = Math.cos(thetaA);
  const cB = Math.cos(thetaB);

  const e1 = aDir.clone();
  const temp = bDir.clone().sub(e1.clone().multiplyScalar(bDir.dot(e1)));
  const tempLen = temp.length();
  if (tempLen < 1e-6) {
    return { q1: null, q2: null };
  }
  const e2 = temp.divideScalar(tempLen);
  const e3 = new THREE.Vector3().crossVectors(e1, e2).normalize();

  const alpha = bDir.dot(e1);
  const beta = bDir.dot(e2);

  const a = cA;
  const b = (cB - alpha * a) / beta;

  const sq = 1 - a * a - b * b;
  if (sq < 0) {
    return { q1: null, q2: null };
  }
  const c = Math.sqrt(sq);

  const u1 = new THREE.Vector3()
    .addScaledVector(e1, a)
    .addScaledVector(e2, b)
    .addScaledVector(e3, c);

  const u2 = new THREE.Vector3()
    .addScaledVector(e1, a)
    .addScaledVector(e2, b)
    .addScaledVector(e3, -c);

  return {
    q1: u1.normalize().multiplyScalar(radius),
    q2: u2.normalize().multiplyScalar(radius),
  };
}

/**
 * Plane normal of great-circle through two directions, with robust fallback.
 */
export function greatCircleNormal(
  fromDir: THREE.Vector3,
  toDir: THREE.Vector3
): THREE.Vector3 {
  let n = new THREE.Vector3().crossVectors(fromDir, toDir);
  if (n.lengthSq() < 1e-6) {
    // Fallback: any vector ⟂ fromDir
    n.set(0, 1, 0);
    if (Math.abs(n.dot(fromDir)) > 0.99) n.set(1, 0, 0);
    n = new THREE.Vector3().crossVectors(fromDir, n);
  }
  return n.normalize();
}

/**
 * Make sure small positive rotation around normal moves from start → target.
 */
export function orientNormalTowardTarget(
  startDir: THREE.Vector3,
  targetDir: THREE.Vector3,
  normal: THREE.Vector3
): THREE.Vector3 {
  const testStep = 0.01;
  const testDir = startDir.clone().applyAxisAngle(normal, testStep);

  const angleStartToTarget = startDir.angleTo(targetDir);
  const angleTestToTarget = testDir.angleTo(targetDir);

  if (angleTestToTarget > angleStartToTarget) {
    normal.negate();
  }
  return normal;
}

/**
 * Tangent points from PAP to the Pin buffer small circle.
 */
export function findPAPTangentsOnPinBuffer(options: {
  pin: THREE.Vector3,
  pap: THREE.Vector3,
  pinBufferDistance: number,
  ballRadius: number,
  segments?: number,
}): { rightTangent: THREE.Vector3 | null, leftTangent: THREE.Vector3 | null } {
  const { pin, pap, pinBufferDistance, ballRadius, segments = 720 } = options;

  const n = pin.clone().normalize(); // axis of the pin buffer ring
  const theta = pinBufferDistance / ballRadius; // angular radius

  // Basis (u, v) ⟂ n
  let u = new THREE.Vector3(1, 0, 0);
  if (Math.abs(u.dot(n)) > 0.99) u.set(0, 1, 0);
  u.sub(n.clone().multiplyScalar(u.dot(n))).normalize();
  const v = new THREE.Vector3().crossVectors(n, u).normalize();

  const papDir = pap.clone().normalize().multiplyScalar(ballRadius);
  const sideVec = new THREE.Vector3().crossVectors(pin, papDir);

  let bestRightPoint: THREE.Vector3 | null = null;
  let bestLeftPoint: THREE.Vector3 | null = null;
  let bestRightScore = Infinity;
  let bestLeftScore = Infinity;

  for (let i = 0; i <= segments; i++) {
    const phi = (2 * Math.PI * i) / segments;

    const ringDir = u
      .clone()
      .multiplyScalar(Math.cos(phi))
      .add(v.clone().multiplyScalar(Math.sin(phi)));

    const T = n
      .clone()
      .multiplyScalar(Math.cos(theta))
      .add(ringDir.multiplyScalar(Math.sin(theta)))
      .normalize()
      .multiplyScalar(ballRadius);

    const m = new THREE.Vector3().crossVectors(papDir, T);
    if (m.lengthSq() < 1e-6) continue;

    m.normalize();

    const v_gc = new THREE.Vector3().crossVectors(m, T).normalize();
    const v_ring = new THREE.Vector3().crossVectors(n, T).normalize();

    const dot = THREE.MathUtils.clamp(v_gc.dot(v_ring), -1, 1);
    const score = 1 - Math.abs(dot); // 0 = perfect tangency

    const orientation = Math.sign(sideVec.dot(T));

    if (orientation >= 0) {
      if (score < bestRightScore) {
        bestRightScore = score;
        bestRightPoint = T.clone();
      }
    } else {
      if (score < bestLeftScore) {
        bestLeftScore = score;
        bestLeftPoint = T.clone();
      }
    }
  }

  return { rightTangent: bestRightPoint, leftTangent: bestLeftPoint };
}

/**
 * Small-circle arc around an axis, centered at `midPointOnRing`,
 * with a given arc length (inches) on that ring.
 */
export function drawSmallCircleArcAroundAxis(options: {
  ball: THREE.Object3D,
  midPointOnRing: THREE.Vector3,
  axis: THREE.Vector3,
  arcLengthInches: number,
  color: number,
  segments?: number,
}) {
  const {
    ball,
    midPointOnRing,
    axis,
    ringRadiusInches,
    arcLengthInches,
    color,
    segments = 128,
  } = options;

  const ringTheta = ringRadiusInches / ballRadius;
  const sinRingTheta = Math.sin(ringTheta);
  if (sinRingTheta <= 1e-6 || arcLengthInches <= 0) return;

  // s = R * sin(theta) * dPhi  =>  dPhi = s / (R * sin(theta))
  const totalPhi = arcLengthInches / (ballRadius * sinRingTheta);

  const arcPoints = generateArc({
    center: midPointOnRing.clone().normalize().multiplyScalar(ballRadius),
    axis: axis.clone().normalize(),
    radius: ballRadius,
    angleSweep: totalPhi,
    handednessFactor: 1,
    arcOffset,
    segments,
  });

  ball.add(createArcMesh(arcPoints, color));
}
